import { Component, OnInit, DestroyRef, inject, signal, WritableSignal, computed, input, output, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { SearchTerm } from '../../../../models/search-term';
import { ClientInformation } from '../../../../models/client-information';
//import { ClientIntegrationService } from '../../services/client-integration.service';
import { IntegrationErrorsService } from '../../../../services/integration-errors.service';
import { ToastService } from '../../../../services/toast.service';
import { ServiceError } from 'src/app/models/service-error';
import { ErrorCategory } from 'src/app/models/error-category';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { LTFilterService } from 'src/app/features/filter/services/lt-filter.service';
import { LTHelperUtils } from 'src/app/utilities/lt-helper.utils';
import { LTFilterRule } from 'src/app/features/filter/models/lt-filter.models';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GridTemplateDirective } from 'src/app/features/grid/classes/grid-template.directive';
import { LTButtonComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-button/lt-button.component';
import { LTCurrentStatusToggleComponent } from 'src/app/features/lt-errors/components/lt-current-status-toggle/lt-current-status-toggle';
import { LTPngTableComponent } from 'src/app/features/grid/components/lt-png-table/lt-png-table.component';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';
import { LTSearchCardComponent } from 'src/app/features/lt-shared-ui/lt-card/components/lt-search-card/lt-search-card';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';

@Component({
  selector: 'app-lt-service-errors-grid',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    GridTemplateDirective,
    LTButtonComponent,
    LTCurrentStatusToggleComponent,
    LTPngTableComponent,
    LTSpinnerComponent,
    LTSearchCardComponent,
  ],
  templateUrl: './lt-service-errors-grid.component.html',
  styleUrls: ['./lt-service-errors-grid.component.scss'],
  standalone: true,
})
export class LTServiceErrorsGridComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly errorService = inject(IntegrationErrorsService);
  private readonly toastService = inject(ToastService);
  private readonly filterService = inject(LTFilterService);

  //#region inputs-outputs
  data = input<ServiceError[]>();
  clients = input<ClientInformation[]>();
  loading = input<boolean>();
  categories = input<ErrorCategory[]>();
  statuses = input<CurrentStatus[]>();

  errorUpdated = output<ServiceError>();
  //#endregion

  //#region status signals
  isUpdating = signal(false);
  statusUpdating = signal<boolean>(false);
  //#endregion

  //#region selection variables
  selectedError = signal<ServiceError>(null);
  //#endregion

  //#region search vars
  public stackSearchTerm: SearchTerm;
  public logSearchTerm: SearchTerm;
  private stackSearchValue = signal<string>('');
  private logSearchValue = signal<string>('');
  //#endregion

  //#region error status vars
  private statusSearchTerm: WritableSignal<string> = signal<string>('');
  hasStatusFilter = signal<boolean>(false);
  statusSelectedFilter = signal<any>(null);
  //#endregion

  //#region PNG-Vars
  errorsGrid = signal<GridSettings>(null);
  errorsGridReady = signal<boolean>(false);
  gridLoading = signal<boolean>(false);

  errorColumns: GridColumnData[] = [
    new GridColumnData({ 
      width: '250px',
      field: 'errorCategoryPriority', 
      header: 'Category-Priority', 
      dataType: 'text', 
      type: 'text', 
      displayFunction: (value: any, rowData: any) => `${rowData.errorCategoryName}-${rowData.errorCategoryPriority}`,
      exportFunction: (value: any, rowData: any) => `${rowData.errorCategoryName}`,
      display: true, 
      exportable: false, 
      filterable: false,
      bodyStyleClass: 'text-wrap',
     }),
    new GridColumnData({ width: '250px', field: 'errorCategoryName', header: 'Category', dataType: 'text', type: 'text', display: true, exportable: false, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '150px', field: 'serverId', header: 'Server Id', exportHeader: 'Server Id', dataType: 'text', type: 'text', display: true, exportable: true, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '100px', field: 'criticalError', header: 'Critical Error', dataType: 'boolean', type: 'boolean', display: true, exportable: false }),
    new GridColumnData({ width: '200px', field: 'receivedDate', header: 'Date Received', exportHeader: 'Error Last Received', dataType: 'date', type: 'date', format: 'MM/dd/yyyy, h:mm:ss a', display: true, exportable: true, filterable: false, bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '500px', field: 'logMessage', header: 'Log Message', dataType: 'text', type: 'text', display: true, exportable: true, pipeFormat: 'truncate:200', bodyStyleClass: 'text-wrap' }),
    new GridColumnData({ width: '150px', field: 'errorCount', header: '#', exportHeader: 'Error Count', dataType: 'number', type: 'number', display: true, exportable: true }),
    new GridColumnData({ width: '250px', field: 'currentStatusName', header: 'Current Status', dataType: 'number', type: 'number', display: true, exportable: false }),
  ];

  //#endregion

  constructor() {
    this.stackSearchTerm = new SearchTerm();
    this.logSearchTerm = new SearchTerm();
    effect(() => {
      this.gridLoading.set(this.loading() ?? false);
    });
    effect(() => {
      const _ = this.gridData();
      untracked(() => {
        if (!this.errorsGridReady()) return;
        this.updateGrid();
      });
    });
  }

  ngOnInit(): void {
    this.refresh();
    this.setupGrid();
  }

  //#region data utils

  gridData = computed(() => {
    return this.filteredData();
  });

  filteredData = computed(() => {
    const data = this.enrichedData();
    const hasFilter = this.hasStatusFilter();
    const filterValue = this.statusSearchTerm();
    const searchFilters = this.searchFilters();

    if ((!hasFilter || filterValue === null) && searchFilters.length === 0) return data;

    let filtered = data;

    if (hasFilter && filterValue !== null){
      filtered = this.filterService.filterBy(
        filtered, 'currentStatusName', filterValue,
      );
    }
    
    if (searchFilters.length > 0) {
      const currentSearchFilterList = this.filterService.createFilterGroup('and', searchFilters);
      filtered = this.filterService.applyFilter(filtered, currentSearchFilterList);
    }

    return filtered;
  });

  enrichedData = computed(() => {
    const errors = this.data();
    const statuses = this.statuses();

    return errors.map(error => this.enrichErrorWithInformation(
      error, 
      statuses,
    ));
  });

  private enrichErrorWithInformation(
    error: ServiceError, 
    statuses: CurrentStatus[],
  ): ServiceError {
    const enrichedError = { ...error };

    enrichedError.currentStatusName = LTHelperUtils.getCurrentStatus(statuses, error.currentStatusId)?.statusName;

    return enrichedError;
  }
  
  //#endregion
  
  //#region Grid Setup - Update Functions
  setupGrid(): void {
    const data = this.gridData();

    const settings = new GridSettings ({
      data: data,
      fixedGrid: true,
      columns: this.errorColumns,
      loading: this.gridLoading,
      tableSize: 'small',
      sortMode: 'multiple',
      resizableColumns: true,
      reorderableColumns: true,
      multisort: [
        {
          field: 'receivedDate',
          order: 1,
        },
      ],
      sort: {
        field: 'receivedDate',
        order: 1,
      },
      dataKey: 'serviceErrorId',
      rowExpansion: {
        enabled: true,
        expandOnRowClick: false,
        allowMultiple: true,
        expandTemplate: 'expandedrow',
      },
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: 'ServiceErrors',
        renderTemplates: false,
        header: 'exportHeader',
      },
      tableStyle: { 'min-width': 'auto' },
    });
    
    this.errorsGrid.set(settings);
    this.errorsGridReady.set(true);
  }

  updateGrid(): void {
    const dataItems = this.gridData();

    this.errorsGrid.set(new GridSettings({
      ...this.errorsGrid(),
      data: dataItems,
      loading: this.gridLoading,
    }));
  }
  
  //#endregion

  //#region Refresh

  public refresh(): void {
  }

  //#endregion

  updateError(error: ServiceError, currentStatusName: string) {
    if (this.isUpdating()) {
      return;
    }

    this.selectedError.set(error);
    const selectedStatus = LTHelperUtils.getCurrentStatusByName(this.statuses(), currentStatusName);

    if (!selectedStatus) {
      this.toastService.toastCreate('Invalid status selected', 'Warning');
      return;
    }

    this.statusUpdating.set(true);

    this.selectedError.update(err => ({
      ...err,
      currentStatusId: selectedStatus.currentStatusId,
      criticalError: currentStatusName === 'Resolved' ? false : err.criticalError,
    }));

    this.errorService.updateServiceError(this.selectedError())
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe(
      (_value) => {
        this.errorUpdated.emit(this.selectedError());
      });
  }

  //#region Filtering

  searchFilters = computed(() => {
    const filters: LTFilterRule[] = [];
    const stackVal = this.stackSearchValue();
    const logVal = this.logSearchValue();
    if (stackVal) {
      filters.push({ field: 'stackTrace', value: stackVal, operator: 'contains', ignoreCase: true });
    }
    if (logVal) {
      filters.push({ field: 'logMessage', value: logVal, operator: 'contains', ignoreCase: true });
    }
    return filters;
  });

  clearFilters() {
    this.stackSearchTerm.clear();
    this.logSearchTerm.clear();
    this.stackSearchValue.set('');
    this.logSearchValue.set('');
    this.statusSearchTerm.set(null);
    this.hasStatusFilter.set(false);
  }

  //#region Status Filter

  onColumnStatusFilter(filterValue: string): void {
    const previousFilter = this.statusSearchTerm();
    if (filterValue === previousFilter && filterValue !== null){
      this.clearStatusFilter();
      return;
    }

    this.hasStatusFilter.set(true);
    this.statusSearchTerm.set(filterValue);
    // Grid will auto-update due to computed filteredData
    //this.updateGrid();
  }

  clearStatusFilter(): void {
    this.statusSearchTerm.set(null);
    this.hasStatusFilter.set(false);
    this.statusSelectedFilter.set(null);
  }

  //#endregion

  //#region search utils

  clearSearch(item: SearchTerm) {
    if (item === this.stackSearchTerm) this.stackSearchValue.set('');
    else if (item === this.logSearchTerm) this.logSearchValue.set('');
  }

  updateSearch(item: SearchTerm) {
    if (item === this.stackSearchTerm) this.stackSearchValue.set(item.currentSearchValue);
    else if (item === this.logSearchTerm) this.logSearchValue.set(item.currentSearchValue);
  }

  //#endregion

  //#endregion

  navigateToIntegrationsByServer(dataItem: ServiceError) {
    this.router.navigate(['/serverintegrations', dataItem.serverId]);
  }
}
